import { useEffect, useState } from "react";
import api from "../lib/api";
import { Coins, ShieldCheck, Swords, X, Loader2, Heart, Sword, Crown, Gem } from "lucide-react";

const TIER_BADGE_CSS = {
    rusty: "bg-[#C97818] text-white",
    bronze: "bg-[#B45309] text-white",
    iron: "bg-[#6B7280] text-white",
    gold: "bg-[#FBBF24] text-ink",
};

export default function MmoShopModal({
    open,
    onClose,
    credits,
    currentTier,
    currentWeapon = "",
    currentHat = "",
    currentNeck = "",
    combatLevel = 1,
    onPurchased,
}) {
    const [items, setItems] = useState([]);
    const [weapons, setWeapons] = useState([]);
    const [hats, setHats] = useState([]);
    const [necklaces, setNecklaces] = useState([]);
    const [equippedWeapon, setEquippedWeapon] = useState(currentWeapon);
    const [equippedHat, setEquippedHat] = useState(currentHat);
    const [equippedNeck, setEquippedNeck] = useState(currentNeck);
    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => { setEquippedWeapon(currentWeapon); }, [currentWeapon]);
    useEffect(() => { setEquippedHat(currentHat); }, [currentHat]);
    useEffect(() => { setEquippedNeck(currentNeck); }, [currentNeck]);

    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        setLoading(true);
        Promise.all([
            api.get("/mmo/shop"),
            api.get("/mmo/weapons"),
            api.get("/mmo/cosmetics"),
        ])
            .then(([tiersRes, weaponsRes, cosRes]) => {
                if (!alive) return;
                setItems(tiersRes.data);
                setWeapons(weaponsRes.data?.items || []);
                setEquippedWeapon(weaponsRes.data?.equipped || "");
                setHats(cosRes.data?.hats || []);
                setNecklaces(cosRes.data?.necklaces || []);
                setEquippedHat(cosRes.data?.equipped_hat || "");
                setEquippedNeck(cosRes.data?.equipped_neck || "");
            })
            .catch(() => { /* ignore */ })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [open]);

    if (!open) return null;
    const curOrder = items.find((i) => i.id === currentTier)?.order ?? -1;

    const buyTier = async (tier) => {
        setErr("");
        setBusy(`tier:${tier}`);
        try {
            const { data } = await api.post("/mmo/shop/buy", { tier });
            if (onPurchased) onPurchased(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn't buy");
        } finally {
            setBusy(null);
        }
    };

    const buyWeapon = async (weapon) => {
        setErr("");
        setBusy(`weapon:${weapon}`);
        try {
            const { data } = await api.post("/mmo/weapons/buy", { weapon });
            setEquippedWeapon(data.weapon);
            // Refresh weapon ownership flags
            try {
                const { data: w } = await api.get("/mmo/weapons");
                setWeapons(w?.items || []);
                setEquippedWeapon(w?.equipped || data.weapon);
            } catch { /* ignore */ }
            if (onPurchased) onPurchased(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn't buy");
        } finally {
            setBusy(null);
        }
    };

    const buyCosmetic = async (slot, item) => {
        setErr("");
        setBusy(`${slot}:${item}`);
        try {
            const { data } = await api.post("/mmo/cosmetics/buy", { slot, item });
            try {
                const { data: cos } = await api.get("/mmo/cosmetics");
                setHats(cos?.hats || []);
                setNecklaces(cos?.necklaces || []);
                setEquippedHat(cos?.equipped_hat || "");
                setEquippedNeck(cos?.equipped_neck || "");
            } catch { /* ignore */ }
            if (onPurchased) onPurchased(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn't buy");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="mmo-shop-modal">
            <div className="brutal-card p-5 sm:p-6 bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h2 className="font-heading font-black text-3xl text-ink tracking-tighter">Armoury</h2>
                        <p className="font-body text-sm text-ink/60">More HP. More damage. Buy with banked credits.</p>
                    </div>
                    <button onClick={onClose} className="brutal-btn-white p-1.5" data-testid="mmo-shop-close" aria-label="Close shop">
                        <X className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                </div>

                <div className="flex items-center justify-between bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-2 shadow-brutal mb-4">
                    <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Your credits</span>
                    <span className="font-heading font-black text-xl text-ink tabular-nums" data-testid="mmo-shop-credits">
                        <Coins className="w-4 h-4 inline mr-1 text-ink" strokeWidth={2.5} />{credits ?? 0}
                    </span>
                </div>

                {loading ? (
                    <div className="text-center py-10"><Loader2 className="w-8 h-8 mx-auto text-vibrantorange animate-spin" /></div>
                ) : (
                    <>
                        <h3 className="font-heading font-black text-sm uppercase tracking-widest text-ink/60 mb-2">Armour Sets</h3>
                        <div className="space-y-3" data-testid="mmo-shop-list">
                            {items.map((it) => {
                                const owned = curOrder >= it.order;
                                const canAfford = (credits ?? 0) >= it.price;
                                const meetsLevel = combatLevel >= (it.min_level || 0);
                                const locked = !owned && it.order > curOrder + 1;
                                return (
                                    <div key={it.id} data-testid={`mmo-shop-tier-${it.id}`} className="brutal-card p-3 bg-cream flex items-center gap-3">
                                        <div className="w-14 h-14 rounded-xl border-[3px] border-ink flex items-center justify-center shadow-brutal" style={{ background: it.tint }}>
                                            <ShieldCheck className="w-7 h-7 text-white" strokeWidth={2.5} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <span className={`font-heading font-black text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md border-2 border-ink ${TIER_BADGE_CSS[it.id] || "bg-white text-ink"}`}>{it.name}</span>
                                                {owned && <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-emerald-600">equipped</span>}
                                                {it.min_level > 0 && (
                                                    <span className={`font-heading font-bold text-[10px] uppercase tracking-widest ${meetsLevel ? "text-emerald-600" : "text-vibrantpink"}`}>
                                                        Req lvl {it.min_level}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex gap-3 text-sm font-heading font-bold text-ink">
                                                <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-vibrantpink" strokeWidth={2.5} /> {it.max_hp} HP</span>
                                                <span className="inline-flex items-center gap-1"><Swords className="w-3.5 h-3.5 text-vibrantorange" strokeWidth={2.5} /> {it.damage} dmg</span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-heading font-black text-xl text-ink tabular-nums">{it.price} cr</div>
                                            {owned ? (
                                                <span className="font-body text-xs text-emerald-700">✓ Owned</span>
                                            ) : locked ? (
                                                <span className="font-body text-xs text-ink/40">Locked</span>
                                            ) : !meetsLevel ? (
                                                <span className="font-body text-xs text-vibrantpink">Lvl {it.min_level} needed</span>
                                            ) : (
                                                <button
                                                    onClick={() => buyTier(it.id)}
                                                    disabled={busy === `tier:${it.id}` || !canAfford}
                                                    data-testid={`mmo-shop-buy-${it.id}`}
                                                    className="brutal-btn-primary py-1 px-3 text-xs mt-0.5"
                                                >
                                                    {busy === `tier:${it.id}` ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.5} /> : canAfford ? "Buy" : "Need more"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {weapons.length > 0 && (
                            <>
                                <h3 className="font-heading font-black text-sm uppercase tracking-widest text-ink/60 mt-5 mb-2">Weapons</h3>
                                <div className="space-y-3" data-testid="mmo-shop-weapons">
                                    {weapons.map((w) => {
                                        const equipped = equippedWeapon === w.id;
                                        const owned = w.owned;
                                        const canAfford = (credits ?? 0) >= w.price;
                                        const meetsLevel = combatLevel >= (w.min_level || 0);
                                        const dmgPct = Math.round((w.damage_mult - 1) * 100);
                                        return (
                                            <div key={w.id} data-testid={`mmo-shop-weapon-${w.id}`} className="brutal-card p-3 bg-cream flex items-center gap-3">
                                                <div className="w-14 h-14 rounded-xl border-[3px] border-ink flex items-center justify-center shadow-brutal" style={{ background: w.tint }}>
                                                    <Sword className="w-7 h-7 text-white" strokeWidth={2.5} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <span className="font-heading font-black text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md border-2 border-ink bg-[#6B7280] text-white">
                                                            {w.name}
                                                        </span>
                                                        {equipped && <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-emerald-600">wielded</span>}
                                                        {w.min_level > 0 && (
                                                            <span className={`font-heading font-bold text-[10px] uppercase tracking-widest ${meetsLevel ? "text-emerald-600" : "text-vibrantpink"}`}>
                                                                Req lvl {w.min_level}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-3 text-sm font-heading font-bold text-ink">
                                                        <span className="inline-flex items-center gap-1"><Swords className="w-3.5 h-3.5 text-vibrantorange" strokeWidth={2.5} /> +{dmgPct}% damage</span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-heading font-black text-xl text-ink tabular-nums">{w.price} cr</div>
                                                    {equipped ? (
                                                        <span className="font-body text-xs text-emerald-700">✓ Wielded</span>
                                                    ) : !meetsLevel ? (
                                                        <span className="font-body text-xs text-vibrantpink">Lvl {w.min_level} needed</span>
                                                    ) : (
                                                        <button
                                                            onClick={() => buyWeapon(w.id)}
                                                            disabled={busy === `weapon:${w.id}` || (!owned && !canAfford)}
                                                            data-testid={`mmo-shop-buy-${w.id}`}
                                                            className="brutal-btn-primary py-1 px-3 text-xs mt-0.5"
                                                        >
                                                            {busy === `weapon:${w.id}` ? (
                                                                <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.5} />
                                                            ) : owned ? "Wield" : canAfford ? "Buy" : "Need more"}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {hats.length > 0 && (
                            <>
                                <h3 className="font-heading font-black text-sm uppercase tracking-widest text-ink/60 mt-5 mb-2">Hats</h3>
                                <div className="space-y-3" data-testid="mmo-shop-hats">
                                    {hats.map((h) => {
                                        const equipped = equippedHat === h.id;
                                        const owned = h.owned;
                                        const canAfford = (credits ?? 0) >= h.price;
                                        const meetsLevel = combatLevel >= (h.min_level || 0);
                                        return (
                                            <div key={h.id} data-testid={`mmo-shop-hat-${h.id}`} className="brutal-card p-3 bg-cream flex items-center gap-3">
                                                <div className="w-14 h-14 rounded-xl border-[3px] border-ink flex items-center justify-center shadow-brutal" style={{ background: h.color }}>
                                                    <Crown className="w-7 h-7 text-ink" strokeWidth={2.5} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <span className="font-heading font-black text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md border-2 border-ink bg-vibrantyellow text-ink">{h.name}</span>
                                                        {equipped && <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-emerald-600">worn</span>}
                                                    </div>
                                                    <div className="flex gap-3 text-sm font-heading font-bold text-ink">
                                                        <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-vibrantpink" strokeWidth={2.5} /> +{h.max_hp_add} HP</span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-heading font-black text-xl text-ink tabular-nums">{h.price} cr</div>
                                                    {equipped ? (
                                                        <span className="font-body text-xs text-emerald-700">✓ Worn</span>
                                                    ) : !meetsLevel ? (
                                                        <span className="font-body text-xs text-vibrantpink">Lvl {h.min_level} needed</span>
                                                    ) : (
                                                        <button
                                                            onClick={() => buyCosmetic("hat", h.id)}
                                                            disabled={busy === `hat:${h.id}` || (!owned && !canAfford)}
                                                            data-testid={`mmo-shop-buy-${h.id}`}
                                                            className="brutal-btn-primary py-1 px-3 text-xs mt-0.5"
                                                        >
                                                            {busy === `hat:${h.id}` ? (
                                                                <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.5} />
                                                            ) : owned ? "Wear" : canAfford ? "Buy" : "Need more"}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {necklaces.length > 0 && (
                            <>
                                <h3 className="font-heading font-black text-sm uppercase tracking-widest text-ink/60 mt-5 mb-2">Necklaces</h3>
                                <div className="space-y-3" data-testid="mmo-shop-necklaces">
                                    {necklaces.map((n) => {
                                        const equipped = equippedNeck === n.id;
                                        const owned = n.owned;
                                        const canAfford = (credits ?? 0) >= n.price;
                                        const meetsLevel = combatLevel >= (n.min_level || 0);
                                        const dmgPct = Math.round((n.damage_mult - 1) * 100);
                                        return (
                                            <div key={n.id} data-testid={`mmo-shop-neck-${n.id}`} className="brutal-card p-3 bg-cream flex items-center gap-3">
                                                <div className="w-14 h-14 rounded-xl border-[3px] border-ink flex items-center justify-center shadow-brutal" style={{ background: n.color }}>
                                                    <Gem className="w-7 h-7 text-white" strokeWidth={2.5} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <span className="font-heading font-black text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md border-2 border-ink bg-[#dc2626] text-white">{n.name}</span>
                                                        {equipped && <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-emerald-600">worn</span>}
                                                    </div>
                                                    <div className="flex gap-3 text-sm font-heading font-bold text-ink">
                                                        <span className="inline-flex items-center gap-1"><Swords className="w-3.5 h-3.5 text-vibrantorange" strokeWidth={2.5} /> +{dmgPct}% damage</span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-heading font-black text-xl text-ink tabular-nums">{n.price} cr</div>
                                                    {equipped ? (
                                                        <span className="font-body text-xs text-emerald-700">✓ Worn</span>
                                                    ) : !meetsLevel ? (
                                                        <span className="font-body text-xs text-vibrantpink">Lvl {n.min_level} needed</span>
                                                    ) : (
                                                        <button
                                                            onClick={() => buyCosmetic("neck", n.id)}
                                                            disabled={busy === `neck:${n.id}` || (!owned && !canAfford)}
                                                            data-testid={`mmo-shop-buy-${n.id}`}
                                                            className="brutal-btn-primary py-1 px-3 text-xs mt-0.5"
                                                        >
                                                            {busy === `neck:${n.id}` ? (
                                                                <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.5} />
                                                            ) : owned ? "Wear" : canAfford ? "Buy" : "Need more"}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </>
                )}

                {err && <p className="mt-3 font-body text-sm text-vibrantpink font-semibold" data-testid="mmo-shop-error">{err}</p>}

                <p className="mt-4 font-body text-[11px] text-ink/50">
                    Upgrades apply immediately (full heal on equip) and persist across sessions. Armour must be bought in order.
                </p>
            </div>
        </div>
    );
}
